import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../store';
import { fetchSettings, updateSettings } from '../store/slices/settingsSlice';
import { addNotification } from '../store/slices/uiSlice';
import { Formik, Form, Field, ErrorMessage, FieldArray } from 'formik';
import * as Yup from 'yup';
import { FiSave, FiPlus, FiTrash2 } from 'react-icons/fi';

// Validation schema
const SettingsSchema = Yup.object().shape({
    Servers: Yup.array().of(
        Yup.object().shape({
            Host: Yup.string().required('Host is required'),
            Port: Yup.number().required('Port is required').min(1).max(65535),
        })
    ).min(1, 'At least one server is required'),
    UserName: Yup.string().required('Username is required'),
    SecretStoreName: Yup.string().required('Secret store name is required'),
    SecretKey: Yup.string().required('Secret key is required'),
    UseTls: Yup.boolean(),
    Scheme: Yup.string().required('Scheme is required'),
    DatabaseName: Yup.string().required('Database name is required'),
    AuthenticationMechanism: Yup.string().required('Authentication mechanism is required'),
    AuthenticationSource: Yup.string().required('Authentication source is required'),
    TenantsCollectionName: Yup.string().required('Tenants collection name is required'),
    ClustersCollectionName: Yup.string().required('Clusters collection name is required'),
});

const SettingsPage: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { settings, loading, error } = useSelector((state: RootState) => state.settings);
    const [initialValues, setInitialValues] = useState({
        Servers: [{ Host: '', Port: 27017 }],
        UserName: '',
        SecretStoreName: '',
        SecretKey: '',
        UseTls: true,
        Scheme: '',
        DatabaseName: '',
        AuthenticationMechanism: '',
        AuthenticationSource: '',
        TenantsCollectionName: '',
        ClustersCollectionName: '',
    });

    useEffect(() => {
        dispatch(fetchSettings());
    }, [dispatch]);

    useEffect(() => {
        if (settings) {
            setInitialValues(settings);
        }
    }, [settings]);

    const handleSubmit = async (values: any) => {
        try {
            await dispatch(updateSettings(values)).unwrap();

            dispatch(
                addNotification({
                    type: 'success',
                    message: 'Database settings were successfully updated.',
                })
            );
        } catch (error: any) {
            dispatch(
                addNotification({
                    type: 'error',
                    title: 'Settings Update Failed',
                    message: error.message || 'Failed to update database settings.',
                })
            );
        }
    };

    if (loading && !settings) {
        return (
            <div className="flex justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Database Settings</h1>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                    Edit database connection configuration stored in Azure App Configuration
                </div>
            </div>

            {error && (
                <div className="bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-800 rounded-md p-4 text-sm text-red-600 dark:text-red-300">
                    {error}
                </div>
            )}

            <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                <div className="p-6">
                    <Formik
                        initialValues={initialValues}
                        validationSchema={SettingsSchema}
                        onSubmit={handleSubmit}
                        enableReinitialize
                    >
                        {({ values, isValid, dirty }) => (
                            <Form className="space-y-6">
                                <div className="space-y-8">
                                    {/* Servers */}
                                    <div>
                                        <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                                            MongoDB Servers
                                        </h3>
                                        <div className="mt-5 border-t border-gray-200 dark:border-gray-700 pt-5">
                                            <FieldArray name="Servers">
                                                {({ remove, push }) => (
                                                    <div className="space-y-3">
                                                        {values.Servers.map((server, index) => (
                                                            <div key={index} className="flex items-center space-x-2">
                                                                <div className="flex-grow grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                                    <div>
                                                                        <label htmlFor={`Servers.${index}.Host`} className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                                            Host
                                                                        </label>
                                                                        <Field
                                                                            name={`Servers.${index}.Host`}
                                                                            type="text"
                                                                            className="mt-1 shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md"
                                                                        />
                                                                        <ErrorMessage name={`Servers.${index}.Host`} component="div" className="mt-1 text-xs text-red-600 dark:text-red-400" />
                                                                    </div>
                                                                    <div>
                                                                        <label htmlFor={`Servers.${index}.Port`} className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                                            Port
                                                                        </label>
                                                                        <Field
                                                                            name={`Servers.${index}.Port`}
                                                                            type="number"
                                                                            className="mt-1 shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md"
                                                                        />
                                                                        <ErrorMessage name={`Servers.${index}.Port`} component="div" className="mt-1 text-xs text-red-600 dark:text-red-400" />
                                                                    </div>
                                                                </div>
                                                                {values.Servers.length > 1 && (
                                                                    <button
                                                                        type="button"
                                                                        className="flex-shrink-0 mt-5 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                                                                        onClick={() => remove(index)}
                                                                    >
                                                                        <FiTrash2 className="h-5 w-5" />
                                                                    </button>
                                                                )}
                                                            </div>
                                                        ))}
                                                        <button
                                                            type="button"
                                                            className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                                                            onClick={() => push({ Host: '', Port: 27017 })}
                                                        >
                                                            <FiPlus className="mr-2" />
                                                            Add Server
                                                        </button>
                                                    </div>
                                                )}
                                            </FieldArray>
                                        </div>
                                    </div>

                                    {/* Authentication */}
                                    <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                                        <div className="sm:col-span-3">
                                            <label htmlFor="UserName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                Username
                                            </label>
                                            <div className="mt-1">
                                                <Field
                                                    name="UserName"
                                                    type="text"
                                                    className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md"
                                                />
                                            </div>
                                            <ErrorMessage name="UserName" component="div" className="mt-1 text-sm text-red-600 dark:text-red-400" />
                                        </div>

                                        <div className="sm:col-span-3">
                                            <label htmlFor="DatabaseName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                Database Name
                                            </label>
                                            <div className="mt-1">
                                                <Field
                                                    name="DatabaseName"
                                                    type="text"
                                                    className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md"
                                                />
                                            </div>
                                            <ErrorMessage name="DatabaseName" component="div" className="mt-1 text-sm text-red-600 dark:text-red-400" />
                                        </div>

                                        <div className="sm:col-span-2">
                                            <label htmlFor="Scheme" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                Scheme
                                            </label>
                                            <div className="mt-1">
                                                <Field
                                                    as="select"
                                                    name="Scheme"
                                                    className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md"
                                                >
                                                    <option value="">Select scheme</option>
                                                    <option value="MongoDB">MongoDB</option>
                                                    <option value="MongoDBPlusSrv">MongoDBPlusSrv</option>
                                                </Field>
                                            </div>
                                            <ErrorMessage name="Scheme" component="div" className="mt-1 text-sm text-red-600 dark:text-red-400" />
                                        </div>

                                        <div className="sm:col-span-2">
                                            <label htmlFor="AuthenticationMechanism" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                Auth Mechanism
                                            </label>
                                            <div className="mt-1">
                                                <Field
                                                    as="select"
                                                    name="AuthenticationMechanism"
                                                    className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md"
                                                >
                                                    <option value="">Select mechanism</option>
                                                    <option value="SCRAM-SHA-1">SCRAM-SHA-1</option>
                                                    <option value="SCRAM-SHA-256">SCRAM-SHA-256</option>
                                                    <option value="MONGODB-CR">MONGODB-CR</option>
                                                </Field>
                                            </div>
                                            <ErrorMessage name="AuthenticationMechanism" component="div" className="mt-1 text-sm text-red-600 dark:text-red-400" />
                                        </div>

                                        <div className="sm:col-span-2">
                                            <label htmlFor="AuthenticationSource" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                Auth Source
                                            </label>
                                            <div className="mt-1">
                                                <Field
                                                    name="AuthenticationSource"
                                                    type="text"
                                                    className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md"
                                                />
                                            </div>
                                            <ErrorMessage name="AuthenticationSource" component="div" className="mt-1 text-sm text-red-600 dark:text-red-400" />
                                        </div>

                                        <div className="sm:col-span-2">
                                            <label htmlFor="UseTls" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                TLS Settings
                                            </label>
                                            <div className="mt-3">
                                                <div className="flex items-center">
                                                    <Field
                                                        type="checkbox"
                                                        name="UseTls"
                                                        id="UseTls"
                                                        className="focus:ring-primary-500 h-4 w-4 text-primary-600 border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded"
                                                    />
                                                    <label htmlFor="UseTls" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                                                        Use TLS
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Secret Management */}
                                    <div>
                                        <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                                            Secret Management
                                        </h3>
                                        <div className="mt-5 border-t border-gray-200 dark:border-gray-700 pt-5 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                                            <div className="sm:col-span-3">
                                                <label htmlFor="SecretStoreName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                    Secret Store Name
                                                </label>
                                                <div className="mt-1">
                                                    <Field
                                                        name="SecretStoreName"
                                                        type="text"
                                                        className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md"
                                                    />
                                                </div>
                                                <ErrorMessage name="SecretStoreName" component="div" className="mt-1 text-sm text-red-600 dark:text-red-400" />
                                            </div>

                                            <div className="sm:col-span-3">
                                                <label htmlFor="SecretKey" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                    Secret Key
                                                </label>
                                                <div className="mt-1">
                                                    <Field
                                                        name="SecretKey"
                                                        type="text"
                                                        className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md"
                                                    />
                                                </div>
                                                <ErrorMessage name="SecretKey" component="div" className="mt-1 text-sm text-red-600 dark:text-red-400" />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Collection Names */}
                                    <div>
                                        <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                                            Collection Configuration
                                        </h3>
                                        <div className="mt-5 border-t border-gray-200 dark:border-gray-700 pt-5 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                                            <div className="sm:col-span-3">
                                                <label htmlFor="TenantsCollectionName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                    Tenants Collection
                                                </label>
                                                <div className="mt-1">
                                                    <Field
                                                        name="TenantsCollectionName"
                                                        type="text"
                                                        className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md"
                                                    />
                                                </div>
                                                <ErrorMessage name="TenantsCollectionName" component="div" className="mt-1 text-sm text-red-600 dark:text-red-400" />
                                            </div>

                                            <div className="sm:col-span-3">
                                                <label htmlFor="ClustersCollectionName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                    Clusters Collection
                                                </label>
                                                <div className="mt-1">
                                                    <Field
                                                        name="ClustersCollectionName"
                                                        type="text"
                                                        className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md"
                                                    />
                                                </div>
                                                <ErrorMessage name="ClustersCollectionName" component="div" className="mt-1 text-sm text-red-600 dark:text-red-400" />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex justify-end">
                                    <button
                                        type="submit"
                                        disabled={!(isValid && dirty) || loading}
                                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:bg-primary-400 disabled:cursor-not-allowed"
                                    >
                                        {loading ? (
                                            <>
                                                <svg
                                                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <circle
                                                        className="opacity-25"
                                                        cx="12"
                                                        cy="12"
                                                        r="10"
                                                        stroke="currentColor"
                                                        strokeWidth="4"
                                                    ></circle>
                                                    <path
                                                        className="opacity-75"
                                                        fill="currentColor"
                                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                                    ></path>
                                                </svg>
                                                Saving...
                                            </>
                                        ) : (
                                            <>
                                                <FiSave className="mr-2" />
                                                Save Settings
                                            </>
                                        )}
                                    </button>
                                </div>
                            </Form>
                        )}
                    </Formik>
                </div>
            </div>
        </div>
    );
};

export default SettingsPage;